import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const Operations = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Same access PIN used on the Hydraulics page to gate its ADD/REMOVE
  // buttons (Product/Person/Machine/Shift). Reusing the identical value
  // here keeps ADD/REMOVE access consistent across both pages - only
  // Team Leader / HR should be able to add or remove operations.
  const TEAM_LEADER_HR_PIN = "2580";

  const productName = location.state?.productName || "No Product Selected";
  const description =
    location.state?.description || "No Description Available";
  const initialOperations = location.state?.operations || [];
  // Tells us whether we got here from the "Select Product" dropdown or the
  // "Select WFH" dropdown, so the chosen operation is saved into the right
  // column back on the Hydraulics page.
  const operationType = location.state?.operationType || "product";

  // Key used to identify any operations the user adds for this specific
  // product/operationType combination - used both as the localStorage key
  // (same-device cache/fallback) AND as the key under which this
  // product's custom operations live inside the shared JSONBin "bin"
  // (cross-device sync).
  const storageKey = `customOperations_${operationType}_${productName}`;

  // --- Cross-device sync (pure frontend, via JSONBin.io) -------------------
  // localStorage only lives on ONE browser/device, so an operation ADDed
  // or REMOVEd on a laptop was never visible on a phone (and vice-versa).
  // To fix that we reuse the SAME shared JSONBin.io bin that Hydraulics.js
  // already uses for its Product/Person/Machine/Shift lists. We simply add
  // one more field to that bin, "customOperations", which is an object
  // keyed by storageKey (e.g. "customOperations_product_HYDAX 19") whose
  // value is the array of custom operations added for that product.
  //
  // Because JSONBin has no "update one field" endpoint - a PUT always
  // replaces the ENTIRE bin content - every write here first GETs the
  // latest full bin content, merges in just this product's updated
  // operations list, and PUTs the whole object back. That way we never
  // wipe out the products/persons/machines/shifts data that Hydraulics.js
  // keeps in this same bin.
  //
  // These values must match the ones in Hydraulics.js exactly, since it's
  // the same shared bin.
  const JSONBIN_BIN_ID = "6a916555f5f4af5e294d64c5";
  const JSONBIN_API_KEY = "$2a$10$q5yFzvEqzU8NYKWFheK9S.lfRU2FwruXXyEdcgjHZZhU.S2mMvjQK";
  const JSONBIN_BASE_URL = `https://api.jsonbin.io/v3/b/${JSONBIN_BIN_ID}`;

  // Fetches the full current bin content. Returns {} if the request fails
  // (offline, JSONBin down, etc.) so callers can fall back gracefully.
  const fetchFullBin = async () => {
    try {
      const response = await fetch(`${JSONBIN_BASE_URL}/latest`, {
        method: "GET",
        headers: {
          "X-Master-Key": JSONBIN_API_KEY,
          "X-Bin-Meta": "false",
        },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) return {};
      // With X-Bin-Meta: false, JSONBin returns the record itself directly.
      return result || {};
    } catch (e) {
      console.error("Failed to fetch shared bin:", e);
      return {};
    }
  };

  // Pushes a full bin object back to JSONBin (a straight replace).
  const pushFullBin = async (fullBinContent) => {
    try {
      const response = await fetch(JSONBIN_BASE_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Master-Key": JSONBIN_API_KEY,
        },
        body: JSON.stringify(fullBinContent),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "JSONBin rejected the change");
      }
      return true;
    } catch (e) {
      // Saved locally on this device (see localStorage calls below), but
      // the shared bin couldn't be reached so other devices won't see it
      // yet. Logged only - the user still sees the normal success alert
      // so the add/remove itself doesn't feel broken.
      console.error("Failed to sync operation change to JSONBin:", e);
      return false;
    }
  };

  // Given this product's brand-new custom-operations array, fetches the
  // latest full bin (so we don't clobber other products' custom
  // operations, or the products/persons/machines/shifts data Hydraulics.js
  // keeps in the same bin), merges this product's array in under
  // storageKey, and pushes the whole thing back.
  const syncCustomOperationsToServer = async (updatedCustomOperations) => {
    const latestBin = await fetchFullBin();
    const mergedBin = {
      ...latestBin,
      customOperations: {
        ...(latestBin.customOperations || {}),
        [storageKey]: updatedCustomOperations,
      },
    };
    await pushFullBin(mergedBin);
  };

  // Reads whatever custom operations were previously saved (if any) for
  // this product/operationType from the same-device localStorage cache,
  // so we can merge them back in on load even before the server responds.
  const getSavedCustomOperations = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (err) {
      console.error("Failed to read saved operations:", err);
      return [];
    }
  };

  const saveCustomOperationsLocally = (customOperations) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(customOperations));
    } catch (err) {
      console.error("Failed to save operation:", err);
    }
  };

  // Local, editable copy of the operations list so we can append newly
  // added operations to it and have them show up (and be clickable) right
  // away, without touching how the list originally arrived via navigation.
  // On first load, merge in anything previously saved LOCALLY for this
  // product so it isn't lost on refresh, even before the server responds.
  const [operationsList, setOperationsList] = useState(() => {
    const savedCustomOperations = getSavedCustomOperations();
    const merged = [...initialOperations];
    savedCustomOperations.forEach((op) => {
      if (!merged.includes(op)) {
        merged.push(op);
      }
    });
    return merged;
  });

  // Keep the list in sync if the user navigates here again with a
  // different product's operations (also re-merging any locally saved
  // custom operations for that product first).
  useEffect(() => {
    const savedCustomOperations = getSavedCustomOperations();
    const merged = [...initialOperations];
    savedCustomOperations.forEach((op) => {
      if (!merged.includes(op)) {
        merged.push(op);
      }
    });
    setOperationsList(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // On mount (and whenever the product/operationType changes), pull this
  // product's custom operations from the shared bin so an ADD or REMOVE
  // done on ANY device is reflected here too - not just the device that
  // made the change. If the request fails (offline, server down), we
  // simply keep whatever the localStorage-based merge above already put
  // in state, so the page still works.
  useEffect(() => {
    let cancelled = false;

    const fetchServerOperations = async () => {
      const latestBin = await fetchFullBin();
      if (cancelled) return;

      const serverCustomOperations =
        (latestBin.customOperations && latestBin.customOperations[storageKey]) ||
        null;

      if (Array.isArray(serverCustomOperations)) {
        const merged = [...initialOperations];
        serverCustomOperations.forEach((op) => {
          if (!merged.includes(op)) {
            merged.push(op);
          }
        });
        setOperationsList(merged);
        // Keep the local cache in sync with the server so this device
        // still has an up-to-date fallback if it later goes offline.
        saveCustomOperationsLocally(serverCustomOperations);
      }
    };

    fetchServerOperations();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Text typed into the new "Add Operation" field.
  const [newOperation, setNewOperation] = useState("");

  // Session storage key used to remember which operation is currently
  // selected for this operationType, so it can be highlighted green (both
  // immediately on click, and again if the user lands back on this page).
  const selectedStorageKey =
    operationType === "wfh" ? "selectedWFHOperation" : "selectedProductOperation";

  const [selectedOperation, setSelectedOperation] = useState(() => {
    try {
      return sessionStorage.getItem(selectedStorageKey) || "";
    } catch (err) {
      return "";
    }
  });

  // Keep the highlighted selection in sync if the user navigates here again
  // (e.g. for a different product/operationType).
  useEffect(() => {
    try {
      setSelectedOperation(sessionStorage.getItem(selectedStorageKey) || "");
    } catch (err) {
      setSelectedOperation("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSelectOperation = (operation) => {
    // Highlight the clicked operation bright yellow right away.
    setSelectedOperation(operation);

    // Save the chosen operation under the key that matches where we came
    // from, so the previous page (Hydraulics) can read it into the correct
    // column: Product Operation or WFH Operation.
    if (operationType === "wfh") {
      sessionStorage.setItem("selectedWFHOperation", operation);
    } else {
      sessionStorage.setItem("selectedProductOperation", operation);
    }

    // Let the user know which operation they picked before we navigate away.
    alert(`You have selected the operation: "${operation}"`);

    // Go back to the page we came from
    navigate(-1);
  };

  // Adds whatever is typed in the input field to the displayed operations
  // list, saves it locally so it survives a reload on this device, AND
  // pushes it to the shared JSONBin bin so every other device/user sees
  // the new operation too. The user can then click the newly added item
  // (like any other operation) to select it.
  const handleAddOperation = async () => {
    const enteredPin = window.prompt(
      "Adding an operation is restricted to Team Leader / HR.\nEnter the access PIN to continue:"
    );
    if (enteredPin === null) return; // user hit Cancel

    if (enteredPin.trim() !== TEAM_LEADER_HR_PIN) {
      alert("Access Denied: Only Team Leader / HR can add operations.");
      return;
    }

    const value = newOperation.trim();

    if (!value) {
      alert("Please enter an operation before adding.");
      return;
    }

    if (operationsList.includes(value)) {
      alert("This operation is already in the list.");
      return;
    }

    const updatedList = [...operationsList, value];
    setOperationsList(updatedList);
    setNewOperation("");

    // Persist just the custom (user-added) operations for this product,
    // not the original built-in list, so we don't duplicate anything.
    const savedCustomOperations = getSavedCustomOperations();
    const updatedCustomOperations = [...savedCustomOperations, value];

    saveCustomOperationsLocally(updatedCustomOperations);
    await syncCustomOperationsToServer(updatedCustomOperations);

    alert("Added successfully. This operation is now visible to all users.");
  };

  // Removes whatever is typed in the input field from the displayed
  // operations list. If that operation was previously saved as a custom
  // one, it's removed both locally and from the shared bin so it stops
  // appearing for every user, not just this device.
  const handleRemoveOperation = async () => {
    const enteredPin = window.prompt(
      "Removing an operation is restricted to Team Leader / HR.\nEnter the access PIN to continue:"
    );
    if (enteredPin === null) return; // user hit Cancel

    if (enteredPin.trim() !== TEAM_LEADER_HR_PIN) {
      alert("Access Denied: Only Team Leader / HR can remove operations.");
      return;
    }

    const value = newOperation.trim();

    if (!value) {
      alert("Please enter an operation to remove.");
      return;
    }

    if (!operationsList.includes(value)) {
      alert("This operation was not found in the list.");
      return;
    }

    const updatedList = operationsList.filter((op) => op !== value);
    setOperationsList(updatedList);
    setNewOperation("");

    // Remove it from the saved custom operations (if it was a custom one).
    const savedCustomOperations = getSavedCustomOperations();
    const updatedCustomOperations = savedCustomOperations.filter(
      (op) => op !== value
    );

    saveCustomOperationsLocally(updatedCustomOperations);
    await syncCustomOperationsToServer(updatedCustomOperations);

    alert("Removed successfully. This change is now visible to all users.");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "30px",
        background:
          "linear-gradient(135deg,#03045e,#240046,#5a189a,#7209b7)",
        color: "#fff",
      }}
    >
      <style>{`
        @keyframes operationYellowGlow {
          0%, 100% {
            box-shadow: 0 0 10px rgba(255,234,0,0.7);
          }
          50% {
            box-shadow: 0 0 22px rgba(255,234,0,1);
          }
        }
      `}</style>

      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          padding: "30px",
          borderRadius: "20px",
          background: "rgba(255,255,255,0.10)",
          backdropFilter: "blur(20px)",
          textAlign: "left", // Align all content to left
        }}
      >
        <h1>Operations</h1>

        <h2>{productName}</h2>

        <p style={{ fontSize: "18px", lineHeight: "1.8" }}>
          {description}
        </p>

        <h3>Operations</h3>

        <ul
          style={{
            paddingLeft: "20px",
            marginLeft: "0",
            textAlign: "left",
          }}
        >
          {operationsList.map((operation, index) => {
            const isSelected = operation === selectedOperation;
            return (
              <li
                key={index}
                onClick={() => handleSelectOperation(operation)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = "none";
                }}
                style={{
                  marginBottom: "10px",
                  textAlign: "left",
                  cursor: "pointer",
                  color: isSelected ? "#000" : "#fff",
                  fontWeight: isSelected ? "700" : "400",
                  background: isSelected ? "#ffea00" : "transparent",
                  padding: isSelected ? "4px 10px" : "4px 0",
                  borderRadius: isSelected ? "8px" : "0",
                  // Only the selected item needs inline-block (so the
                  // yellow glow box hugs the text). Every other item stays
                  // block-level so each operation renders on its own line
                  // with proper spacing, on both mobile and desktop.
                  display: isSelected ? "inline-block" : "block",
                  animation: isSelected
                    ? "operationYellowGlow 1.4s ease-in-out infinite"
                    : "none",
                  transition: "all 0.2s ease",
                }}
              >
                {operation}
              </li>
            );
          })}
        </ul>

        {/* Add / Remove Operation - lets the user type an operation and
            either add it to the list above, or remove it if it already
            exists in the list. Both actions sync to the shared backend so
            every user sees the same list. */}
        <div style={{ marginTop: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "13px",
            }}
          >
            Add Operation
          </label>

          <input
            type="text"
            value={newOperation}
            onChange={(e) => setNewOperation(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddOperation();
              }
            }}
            placeholder="Enter new operation"
            style={{
              width: "60%",
              height: "50px",
              padding: "0 16px",
              borderRadius: "10px",
              border: "2px solid #d1d5db",
              fontSize: "16px",
              fontWeight: "600",
              background: "#ffffff",
              color: "#000",
              boxSizing: "border-box",
            }}
          />

          <div style={{ display: "flex", gap: "12px", marginTop: "12px" }}>
            <button
              type="button"
              onClick={handleAddOperation}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#16a34a";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#22c55e";
              }}
              style={{
                width: "30%",
                minWidth: "120px",
                height: "50px",
                border: "none",
                borderRadius: "10px",
                fontSize: "14px",
                fontWeight: "700",
                background: "#22c55e",
                color: "#fff",
                cursor: "pointer",
                transition: "background 0.2s ease",
              }}
            >
              ADD
            </button>

            <button
              type="button"
              onClick={handleRemoveOperation}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#dc2626";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#ef4444";
              }}
              style={{
                width: "30%",
                minWidth: "120px",
                height: "50px",
                border: "none",
                borderRadius: "10px",
                fontSize: "14px",
                fontWeight: "700",
                background: "#ef4444",
                color: "#fff",
                cursor: "pointer",
                transition: "background 0.2s ease",
              }}
            >
              REMOVE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Operations;
